/**
 * Shared-device mode for a shop tablet (ONBOARDING J4). A setting of THIS device only (localStorage), turned on by a
 * manager who is signed in on it. While it's on, the app shows only name tiles and check-ins. A tablet-wide lockout
 * (5 wrong PINs → wait 1 minute) sits on top of the server's own per-person lockout.
 */
import { useSyncExternalStore } from "react";

export interface KioskConfig { orgId: string; locationId: string; /** The manager's 4 numbers to leave tablet mode. */ exitPin: string }

const KEY = "logwave.kiosk";
const LOCK_KEY = "logwave.kiosk.lock";
export const MAX_PIN_TRIES = 5;
export const LOCK_MS = 60_000;

const listeners = new Set<() => void>();
let cached: KioskConfig | null | undefined;

function read(): KioskConfig | null {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "null") as Partial<KioskConfig> | null;
    return v && v.orgId && v.locationId && v.exitPin ? (v as KioskConfig) : null;
  } catch { return null; }
}

export function getKiosk(): KioskConfig | null {
  if (cached === undefined) cached = read();
  return cached;
}
export function setKiosk(cfg: KioskConfig | null) {
  try { if (cfg) localStorage.setItem(KEY, JSON.stringify(cfg)); else localStorage.removeItem(KEY); } catch { /* private mode: still on for this tab */ }
  cached = cfg;
  clearPinFails();
  for (const fn of listeners) fn();
}
export function useKiosk(): KioskConfig | null {
  return useSyncExternalStore((fn) => { listeners.add(fn); return () => listeners.delete(fn); }, getKiosk, getKiosk);
}

/* ─────────────── wrong-PIN lockout ─────────────── */

interface LockState { fails: number; until?: number }
let lock: LockState = { fails: 0 };
function saveLock() { try { localStorage.setItem(LOCK_KEY, JSON.stringify(lock)); } catch { /* ignore */ } }
try { const v = JSON.parse(localStorage.getItem(LOCK_KEY) ?? "{}") as Partial<LockState>; lock = { fails: v.fails ?? 0, ...(v.until ? { until: v.until } : {}) }; } catch { /* ignore */ }

/** Milliseconds left in the lockout, or 0. */
export function lockLeft(now = Date.now()): number {
  return lock.until && lock.until > now ? lock.until - now : 0;
}
/** Count one wrong PIN; the fifth in a row locks the tablet for a minute. Returns the lockout left. */
export function pinFailed(now = Date.now()): number {
  lock.fails += 1;
  if (lock.fails >= MAX_PIN_TRIES) lock = { fails: 0, until: now + LOCK_MS };
  saveLock();
  return lockLeft(now);
}
export function clearPinFails() { lock = { fails: 0 }; saveLock(); }
